<template>
  <div class="subscription-pricing-section">
    <!-- Current Subscription Status - Active or Pending -->
    <div v-if="currentSubscription && (currentSubscription.status === 'active' || currentSubscription.status === 'pending')" class="current-subscription-banner">
      <div class="subscription-badge" :class="'status-' + currentSubscription.status">
        <font-awesome-icon :icon="currentSubscription.status === 'active' ? 'crown' : 'clock'" class="status-icon" />
        <span class="status-text">
          {{ currentSubscription.status === 'active' ? ($t('home.activePlan') || 'Active Plan') : ($t('home.pendingPlan') || 'Pending Verification') }}
        </span>
      </div>

      <div class="subscription-info">
        <div class="info-row">
          <span class="info-label">{{ $t('home.status') || 'Status' }}:</span>
          <span class="info-value" :class="'status-' + currentSubscription.status">
            {{ currentSubscription.status === 'active' ? ($t('home.activeStatus') || 'Active') : ($t('home.pendingStatus') || 'Pending') }}
          </span>
        </div>

        <div v-if="currentSubscription.active_from_ts && currentSubscription.active_to_ts" class="info-row">
          <span class="info-label">{{ $t('home.validPeriod') || 'Valid Period' }}:</span>
          <span class="info-value">
            {{ formatDate(currentSubscription.active_from_ts) }} - {{ formatDate(currentSubscription.active_to_ts) }}
          </span>
        </div>

        <div v-if="currentSubscription.status === 'active' && currentSubscription.active_to_ts" class="info-row">
          <span class="info-label">{{ $t('home.daysRemaining') || 'Days Remaining' }}:</span>
          <span class="info-value highlight">
            {{ getDaysRemaining(currentSubscription.active_to_ts) }} {{ $t('home.days') || 'days' }}
          </span>
        </div>

        <div v-if="currentSubscription.status === 'pending'" class="info-row pending-message">
          <font-awesome-icon icon="info-circle" />
          <div class="pending-text">
            <p class="pending-title">{{ $t('home.pendingMessage') || 'Thank you for your payment! We are processing your subscription.' }}</p>
            <p class="pending-timeline">{{ $t('home.pendingTimeline') || 'Your subscription will be activated within 24 hours after we verify your payment. You will receive a notification once it\'s active.' }}</p>
            <p class="contact-info">
              <font-awesome-icon icon="headset" class="contact-icon" />
              <strong>{{ $t('home.needHelp') || 'Need help?' }}</strong>
              {{ $t('home.pendingContact') || 'Contact our support team at' }}
              <a href="mailto:42xchallenge.com@proton.me" class="contact-email">42xchallenge.com@proton.me</a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Suspended Subscription Banner -->
    <div v-if="currentSubscription && currentSubscription.status === 'suspend'" class="current-subscription-banner suspended-banner">
      <div class="subscription-badge status-suspend">
        <font-awesome-icon icon="exclamation-triangle" class="status-icon" />
        <span class="status-text">{{ $t('home.suspendedPlan') || 'Subscription Suspended' }}</span>
      </div>

      <div class="subscription-info">
        <div class="info-row suspended-message">
          <font-awesome-icon icon="info-circle" />
          <div class="suspended-text">
            <p>{{ $t('home.suspendedMessage') || 'Your subscription has been suspended. Please choose a new plan below to continue enjoying premium features.' }}</p>
            <p class="contact-info">
              {{ $t('home.suspendedContact') || 'If you have questions, please contact us at' }}
              <a href="mailto:42xchallenge.com@proton.me" class="contact-email">42xchallenge.com@proton.me</a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!currentSubscription || (currentSubscription.status !== 'active' && currentSubscription.status !== 'pending')">
      <h3 class="pricing-section-title">{{ $t('home.subscriptionPlans') || 'Subscription Plans' }}</h3>
      <p class="pricing-section-subtitle">{{ $t('home.subscriptionDesc') || 'Choose the perfect plan for your running journey' }}</p>

      <!-- Payment Success Banner -->
    <div v-if="paymentSuccess" class="payment-success-banner">
      <div class="banner-icon-wrapper">
        <font-awesome-icon icon="check-circle" class="banner-icon" />
      </div>
      <div class="banner-content">
        <h4 class="banner-title">{{ $t('home.paymentReceived') || 'Payment Confirmation Received!' }}</h4>
        <p class="banner-message">{{ $t('home.paymentConfirmMessage') || 'Thank you! Your subscription will be activated within 24 hours after we verify your payment.' }}</p>
        <p class="banner-note">{{ $t('home.paymentNote') || 'You will receive a notification once your subscription is activated.' }}</p>
      </div>
      <button @click="paymentSuccess = false" class="banner-close">
        <font-awesome-icon icon="times" />
      </button>
    </div>

    <!-- Payment Error Banner -->
    <div v-if="paymentError" class="payment-error-banner">
      <div class="banner-icon-wrapper">
        <font-awesome-icon icon="exclamation-circle" class="banner-icon" />
      </div>
      <div class="banner-content">
        <h4 class="banner-title">{{ $t('home.error') || 'Error' }}</h4>
        <p class="banner-message">{{ paymentErrorMessage }}</p>
        <p class="banner-note">
          {{ $t('home.errorContact') || 'If the problem persists, please contact us at' }}
          <a href="mailto:42xchallenge.com@proton.me" class="banner-email">42xchallenge.com@proton.me</a>
        </p>
      </div>
      <button @click="paymentError = false" class="banner-close">
        <font-awesome-icon icon="times" />
      </button>
    </div>

    <!-- AI Benefits / Hero -->
    <div class="ai-hero">
      <div class="ai-hero-left">
        <h4 class="ai-hero-title">
          <font-awesome-icon icon="fire" />
          {{ $t('home.aiCoachTitle') || 'Unlock your AI Running Coach' }}
        </h4>
        <p class="ai-hero-subtitle">
          {{ $t('home.aiCoachSubtitle') || 'Turn every run into actionable insights. Train smarter, avoid plateaus, and hit new PRs with personalized guidance.' }}
        </p>
        <ul class="ai-hero-points">
          <li v-for="(benefit, i) in aiBenefits" :key="'b-'+i">
            <font-awesome-icon icon="check-circle" />
            <span class="benefit-title">{{ $t(benefit.titleKey) }}</span>
            <span class="benefit-desc">{{ $t(benefit.descKey) }}</span>
          </li>
        </ul>
        <div class="ai-hero-ctas">
          <button class="btn btn-primary" @click="scrollToPlans">
            {{ $t('home.seePlans') || 'See Plans & Pricing' }}
          </button>
        </div>
      </div>
      <div class="ai-hero-right">
        <div class="ai-metrics-card">
          <div class="metric">
            <div class="metric-label">{{ $t('home.weeklyVolume') || 'Weekly Volume' }}</div>
            <div class="metric-value">42.8 km</div>
            <div class="metric-delta positive">+12%</div>
          </div>
          <div class="metric">
            <div class="metric-label">{{ $t('home.easyPace') || 'Easy Pace' }}</div>
            <div class="metric-value">6:02 /km</div>
            <div class="metric-delta positive">+0:08</div>
          </div>
          <div class="metric">
            <div class="metric-label">{{ $t('home.sleepRecovery') || 'Recovery Score' }}</div>
            <div class="metric-value">81</div>
            <div class="metric-delta neutral">=</div>
          </div>
          <div class="metric">
            <div class="metric-label">{{ $t('home.formSignal') || 'Form Signal' }}</div>
            <div class="metric-value">{{ $t('home.stable') || 'Stable' }}</div>
            <div class="metric-delta positive">OK</div>
          </div>
        </div>
        <div class="ai-how-it-works">
          <div class="step" v-for="(step, i) in howItWorksSteps" :key="'s-'+i">
            <div class="step-num">{{ i + 1 }}</div>
            <div class="step-body">
              <div class="step-title">{{ $t(step.titleKey) }}</div>
              <div class="step-desc">{{ $t(step.descKey) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Free vs PRO Comparison -->
    <div class="comparison" v-if="!currentSubscription || (currentSubscription.status !== 'active' && currentSubscription.status !== 'pending')">
      <h4 class="comparison-title">{{ $t('home.compareTitle') || 'Why go PRO?' }}</h4>
      <div class="comparison-grid">
        <div class="comparison-card">
          <div class="comp-header">{{ $t('home.free') || 'Free' }}</div>
          <ul class="comp-list">
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.freeBasicTracking') || 'Basic activity tracking' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.freeHistory') || 'Recent history only' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.freeCommunity') || 'Community feed' }}</li>
          </ul>
        </div>
        <div class="comparison-card pro">
          <div class="comp-header">
            <font-awesome-icon icon="fire" />
            {{ $t('home.pro') || 'PRO' }}
          </div>
          <ul class="comp-list">
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.proSmartInsights') || 'Smart insights after every run' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.proPersonalPlans') || 'Personalized training nudges' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.proFormSignals') || 'Technique & effort signals' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.proTrendlines') || 'Trendlines & progress tracking' }}</li>
            <li><font-awesome-icon icon="check-circle" /> {{ $t('home.proPrioritySupport') || 'Priority support' }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Pricing Cards - Only show if no active/pending subscription -->
    <div v-if="!currentSubscription || (currentSubscription.status !== 'active' && currentSubscription.status !== 'pending')" class="pricing-cards" id="plans">
      <div
        v-for="(plan, idx) in subscriptionPlans"
        :key="idx"
        class="pricing-card"
        :class="{ 'recommended': plan.recommended }"
        @click="openPaymentModal(plan)"
      >
        <div v-if="plan.recommended" class="recommended-badge">
          <font-awesome-icon icon="fire" />
          {{ $t('home.recommended') || 'RECOMMENDED' }}
        </div>
        <div class="plan-header">
          <h4 class="plan-name">{{ plan.name }}</h4>
          <div class="plan-duration">{{ plan.duration }}</div>
        </div>
        <div class="plan-pricing">
          <div class="plan-price">
            <span class="price-amount">{{ plan.price }}</span>
            <span class="price-currency">VND</span>
          </div>
          <div class="plan-price-per-month">{{ plan.pricePerMonth }}/{{ $t('home.month') || 'month' }}</div>
        </div>
        <div v-if="plan.discount > 0" class="plan-discount">
          <font-awesome-icon icon="tag" />
          {{ $t('home.save') || 'Save' }} {{ plan.discount }}%
          <span class="savings-amount">({{ plan.annualSavings }} VND)</span>
        </div>
        <div class="plan-features">
          <div class="feature-item">
            <font-awesome-icon icon="check-circle" class="check-icon" />
            <span>{{ $t('home.unlimitedActivities') || 'Unlimited activities tracking' }}</span>
          </div>
          <div class="feature-item">
            <font-awesome-icon icon="check-circle" class="check-icon" />
            <span>{{ $t('home.aiAnalysis') || 'AI-powered analysis' }}</span>
          </div>
          <div class="feature-item">
            <font-awesome-icon icon="check-circle" class="check-icon" />
            <span>{{ $t('home.prioritySupport') || 'Priority support' }}</span>
          </div>
        </div>
      </div>
    </div>

    </div>

    <!-- Payment Modal -->
    <b-modal
      v-model="showPaymentModal"
      :title="$t('home.subscribeNow') || 'Subscribe Now'"
      size="lg"
      centered
      hide-footer
    >
      <div v-if="selectedPlan" class="payment-modal-content">
        <!-- Plan Summary -->
        <div class="plan-summary">
          <h4 class="plan-summary-title">{{ selectedPlan.name }} {{ $t('home.plan') || 'Plan' }}</h4>
          <div class="plan-summary-details">
            <div class="summary-item">
              <span class="summary-label">{{ $t('home.duration') || 'Duration' }}:</span>
              <span class="summary-value">{{ selectedPlan.duration }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ $t('home.totalPrice') || 'Total Price' }}:</span>
              <span class="summary-value highlight">{{ selectedPlan.price }} VND</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ $t('home.pricePerMonth') || 'Per Month' }}:</span>
              <span class="summary-value">{{ selectedPlan.pricePerMonth }} VND</span>
            </div>
            <div v-if="selectedPlan.discount > 0" class="summary-item discount-item">
              <span class="summary-label">{{ $t('home.youSave') || 'You Save' }}:</span>
              <span class="summary-value save-amount">{{ selectedPlan.annualSavings }} VND ({{ selectedPlan.discount }}%)</span>
            </div>
          </div>
        </div>

        <!-- Strava ID Input (for non-logged-in users) -->
        <div v-if="!isLoggedIn" class="strava-id-input-section">
          <h5 class="strava-id-title">
            <font-awesome-icon icon="user" />
            {{ $t('home.enterStravaId') || 'Enter Your Strava ID' }}
          </h5>
          <p class="strava-id-description">
            {{ $t('home.stravaIdDesc') || 'Please enter your Strava Athlete ID. You can find it in your Strava profile URL.' }}
          </p>
          <div class="strava-id-input-group">
            <input
              v-model="manualStravaId"
              type="text"
              class="strava-id-input"
              :class="{ 'has-error': showStravaIdError }"
              :placeholder="$t('home.stravaIdPlaceholder') || 'e.g., 12345678'"
              @input="validateStravaId"
            />
            <div v-if="manualStravaId && !isValidStravaId" class="strava-id-error">
              <font-awesome-icon icon="exclamation-circle" />
              {{ $t('home.invalidStravaId') || 'Please enter a valid numeric Strava ID' }}
            </div>
          </div>
          <p class="strava-id-help">
            <font-awesome-icon icon="question-circle" />
            {{ $t('home.howToFindStravaId') || 'How to find your Strava ID:' }}
            <a href="https://www.strava.com/settings/profile" target="_blank" rel="noopener">
              {{ $t('home.goToProfile') || 'Go to your profile' }}
            </a>
            {{ $t('home.stravaIdInUrl') || 'and look at the URL (e.g., strava.com/athletes/12345678)' }}
          </p>
        </div>

        <!-- Logged-in user info -->
        <div v-else class="logged-in-user-info">
          <font-awesome-icon icon="check-circle" class="check-icon" />
          <span>{{ $t('home.subscribingAs') || 'Subscribing as' }}: <strong>{{ currentUser._id }}</strong></span>
        </div>

        <!-- Payment Instructions -->
        <div class="payment-instructions">
          <h5 class="instructions-title">
            <font-awesome-icon icon="info-circle" />
            {{ $t('home.paymentInstructions') || 'Payment Instructions' }}
          </h5>

          <!-- Step 1: Bank Transfer -->
          <div class="instruction-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h6>{{ $t('home.bankTransfer') || 'Bank Transfer' }}</h6>
              <div class="bank-details">
                <div class="bank-info-item">
                  <span class="bank-label">{{ $t('home.bankName') || 'Bank' }}:</span>
                  <span class="bank-value">BIDV</span>
                </div>
                <div class="bank-info-item">
                  <span class="bank-label">{{ $t('home.accountNumber') || 'Account Number' }}:</span>
                  <span class="bank-value copyable" @click="copyToClipboard('1660135803')">
                    1660135803
                    <font-awesome-icon icon="copy" class="copy-icon" />
                  </span>
                </div>
                <div class="bank-info-item">
                  <span class="bank-label">{{ $t('home.accountName') || 'Account Name' }}:</span>
                  <span class="bank-value">HOANG THI TAM</span>
                </div>
                <div class="bank-info-item">
                  <span class="bank-label">{{ $t('home.amount') || 'Amount' }}:</span>
                  <span class="bank-value highlight">{{ selectedPlan.price }} VND</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Transfer Note -->
          <div class="instruction-step important">
            <div class="step-number">2</div>
            <div class="step-content">
              <h6>{{ $t('home.transferNote') || 'Transfer Note (Important!)' }}</h6>
              <p class="note-description">{{ $t('home.transferNoteDesc') || 'Please include this exact note in your transfer:' }}</p>
              <div class="transfer-note-box">
                <code>{{ transferNote }}</code>
                <button @click="copyToClipboard(transferNote)" class="copy-btn">
                  <font-awesome-icon icon="copy" />
                  {{ $t('home.copy') || 'Copy' }}
                </button>
              </div>
              <p class="note-warning">
                <font-awesome-icon icon="exclamation-triangle" />
                {{ $t('home.noteWarning') || 'Without this note, we cannot verify your payment!' }}
              </p>
            </div>
          </div>

          <!-- Step 3: QR Code -->
          <div class="instruction-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h6>{{ $t('home.scanQrCode') || 'Scan QR Code' }}</h6>
              <p>{{ $t('home.qrCodeDesc') || 'Use your banking app to scan this QR code:' }}</p>
              <div class="qr-code-container">
                <img :src="qrCodeUrl" alt="Payment QR Code" class="qr-code" />
                <p class="qr-note">{{ $t('home.qrNote') || 'QR code includes account details. Remember to add the transfer note!' }}</p>
              </div>
            </div>
          </div>

          <!-- Step 4: Activation -->
          <div class="instruction-step">
            <div class="step-number">4</div>
            <div class="step-content">
              <h6>{{ $t('home.activationTime') || 'Activation Time' }}</h6>
              <p>
                {{ $t('home.activationDesc') || 'Your subscription will be activated within 24 hours after we verify your payment. You will see the PRO badge on your profile once activated.' }}
              </p>
              <p class="contact-info">
                {{ $t('home.activationContact') || 'If you have any questions, please contact us at' }}
                <a href="mailto:42xchallenge.com@proton.me" class="contact-email">42xchallenge.com@proton.me</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Strava ID Error Banner -->
        <div v-if="showStravaIdError" class="strava-id-error-banner">
          <font-awesome-icon icon="exclamation-triangle" class="error-icon" />
          <span>{{ stravaIdErrorMessage }}</span>
        </div>

        <!-- Action Buttons -->
        <div class="modal-actions">
          <button @click="showPaymentModal = false" class="btn btn-outline-secondary">
            {{ $t('home.cancel') || 'Cancel' }}
          </button>
          <button @click="confirmPayment" class="btn btn-primary">
            <font-awesome-icon icon="check-circle" />
            {{ $t('home.paymentMade') || 'I Have Made the Payment' }}
          </button>
        </div>

        <!-- Copy Success Toast -->
        <div v-if="showCopySuccess" class="copy-success-toast">
          <font-awesome-icon icon="check-circle" />
          {{ $t('home.copiedToClipboard') || 'Copied to clipboard!' }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import $backend from '../backend'
export default {
  name: 'SubscriptionPricing',
  computed: {
    currentUser () {
      const athleteData = localStorage.getItem('athlete')
      if (athleteData && athleteData !== 'undefined') {
        try {
          return JSON.parse(athleteData)
        } catch (e) {
          return null
        }
      }
      return null
    },
    isLoggedIn () {
      return !!(this.currentUser && this.currentUser._id)
    },
    effectiveAthleteId () {
      // Use logged-in user's ID or manually entered Strava ID
      if (this.currentUser && this.currentUser._id) {
        return this.currentUser._id
      }
      return this.manualStravaId || ''
    },
    transferNote () {
      if (!this.selectedPlan || !this.effectiveAthleteId) {
        return ''
      }
      const planCode = this.selectedPlan.name.toLowerCase().replace(/\s+/g, '')
      return `${planCode} ${this.effectiveAthleteId}`
    },
    qrCodeUrl () {
      // Generate VietQR code URL for BIDV
      // Format: https://img.vietqr.io/image/BANK_CODE-ACCOUNT_NUMBER-TEMPLATE.png?amount=AMOUNT&addInfo=NOTE
      if (!this.selectedPlan) {
        return ''
      }
      const bankCode = 'BIDV' // BIDV bank code
      const accountNumber = '1660135803'
      const amount = this.selectedPlan.price.replace(/,/g, '')
      const note = encodeURIComponent(this.transferNote)

      return `https://img.vietqr.io/image/${bankCode}-${accountNumber}-compact2.png?amount=${amount}&addInfo=${note}&accountName=HOANG%20THI%20TAM`
    }
  },
  data () {
    return {
      showPaymentModal: false,
      selectedPlan: null,
      manualStravaId: '',
      isValidStravaId: true,
      showStravaIdError: false,
      stravaIdErrorMessage: '',
      paymentSuccess: false,
      paymentError: false,
      paymentErrorMessage: '',
      aiBenefits: [
        { titleKey: 'home.aiPointPersonalizedInsightsTitle', descKey: 'home.aiPointPersonalizedInsightsDesc' },
        { titleKey: 'home.aiPointTrainSmarterTitle', descKey: 'home.aiPointTrainSmarterDesc' },
        { titleKey: 'home.aiPointSeeProgressTitle', descKey: 'home.aiPointSeeProgressDesc' },
        { titleKey: 'home.aiPointStayMotivatedTitle', descKey: 'home.aiPointStayMotivatedDesc' }
      ],
      howItWorksSteps: [
        { titleKey: 'home.howConnectTitle', descKey: 'home.howConnectDesc' },
        { titleKey: 'home.howAnalyzeTitle', descKey: 'home.howAnalyzeDesc' },
        { titleKey: 'home.howGuidanceTitle', descKey: 'home.howGuidanceDesc' }
      ],
      showCopySuccess: false,
      currentSubscription: null,
      subscriptionLoading: false,
      subscriptionPlans: [
        {
          duration: '1 Month',
          name: 'Trial',
          price: '75,000',
          pricePerMonth: '75,000',
          discount: 0,
          annualSavings: '–',
          recommended: false
        },
        {
          duration: '3 Months',
          name: 'Kickstart',
          price: '210,000',
          pricePerMonth: '70,000',
          discount: 6.67,
          annualSavings: '15,000',
          recommended: false
        },
        {
          duration: '6 Months',
          name: 'Upgrade',
          price: '405,000',
          pricePerMonth: '67,500',
          discount: 10,
          annualSavings: '45,000',
          recommended: false
        },
        {
          duration: '1 Year',
          name: 'Pro Runner',
          price: '747,500',
          pricePerMonth: '~62,292',
          discount: 16.94,
          annualSavings: '152,500',
          recommended: true
        }
      ]
    }
  },
  mounted () {
    this.fetchCurrentSubscription()
  },
  methods: {
    scrollToPlans () {
      const el = document.getElementById('plans')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    async fetchCurrentSubscription () {
      if (!this.currentUser || !this.currentUser._id || !this.currentUser.token || !this.currentUser.token.access_token) {
        return
      }

      try {
        this.subscriptionLoading = true
        const response = await $backend.getAthleteSubscription(
          this.currentUser._id,
          this.currentUser.token.access_token
        )

        if (response && response.data) {
          this.currentSubscription = response.data
          console.log('Subscription data loaded:', this.currentSubscription)
          console.log('Subscription status:', this.currentSubscription.status)
        }
      } catch (error) {
        console.error('Error fetching subscription:', error)
        // Don't show error if subscription not found
        if (error.response && error.response.status === 404) {
          this.currentSubscription = null
        }
      } finally {
        this.subscriptionLoading = false
      }
    },
    openPaymentModal (plan) {
      this.selectedPlan = plan
      this.showPaymentModal = true
      // Clear any previous errors and messages
      this.showStravaIdError = false
      this.stravaIdErrorMessage = ''
      this.paymentSuccess = false
      this.paymentError = false
      this.paymentErrorMessage = ''
    },
    formatDate (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getDaysRemaining (endTimestamp) {
      if (!endTimestamp) return 0
      const now = Math.floor(Date.now() / 1000)
      const diff = endTimestamp - now
      return Math.max(0, Math.ceil(diff / (24 * 60 * 60)))
    },
    validateStravaId () {
      if (!this.manualStravaId) {
        this.isValidStravaId = true
        return
      }
      // Strava IDs are numeric
      this.isValidStravaId = /^\d+$/.test(this.manualStravaId.trim())

      // Clear error when user starts typing
      if (this.showStravaIdError) {
        this.showStravaIdError = false
        this.stravaIdErrorMessage = ''
      }
    },
    copyToClipboard (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopySuccess = true
        setTimeout(() => {
          this.showCopySuccess = false
        }, 2000)
      }).catch(err => {
        console.error('Failed to copy:', err)
      })
    },
    async confirmPayment () {
      // Clear previous errors
      this.showStravaIdError = false
      this.stravaIdErrorMessage = ''

      // Validate Strava ID if not logged in
      if (!this.isLoggedIn) {
        if (!this.manualStravaId) {
          this.showStravaIdError = true
          this.stravaIdErrorMessage = this.$t('home.stravaIdRequired') || 'Please enter your Strava ID.'
          return
        }
        if (!this.isValidStravaId) {
          this.showStravaIdError = true
          this.stravaIdErrorMessage = this.$t('home.invalidStravaIdError') || 'Please enter a valid numeric Strava ID.'
          return
        }
      }

      if (!this.effectiveAthleteId || !this.selectedPlan) {
        this.showStravaIdError = true
        this.stravaIdErrorMessage = this.$t('home.stravaIdRequired') || 'Please enter your Strava ID.'
        return
      }

      try {
        const currentTs = Math.floor(Date.now() / 1000)

        // Prepare subscription data (server will validate and calculate timestamps)
        const subscriptionData = {
          athlete_id: this.effectiveAthleteId,
          plan: {
            name: this.selectedPlan.name,
            duration: this.selectedPlan.duration,
            price: this.selectedPlan.price
          },
          payment_info: {
            amount: parseFloat(this.selectedPlan.price.replace(/,/g, '')),
            currency: 'VND',
            made_at_ts: currentTs
          },
          note: this.transferNote
        }

        console.log('Sending subscription verification:', subscriptionData)

        // Make API call without requiring authentication
        await $backend.createSubscriptionVerification(
          this.effectiveAthleteId,
          null, // No auth token required
          subscriptionData
        )

        // Close modal
        this.showPaymentModal = false

        // Show success banner
        this.paymentSuccess = true
        this.paymentError = false

        // Refresh subscription status
        await this.fetchCurrentSubscription()

        // Scroll to top to see success message
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        console.error('Error submitting payment confirmation:', error)

        // Show error banner
        this.paymentError = true
        this.paymentSuccess = false

        // Set error message based on error type
        if (error.response && error.response.status === 429) {
          this.paymentErrorMessage = this.$t('home.rateLimitError') || 'Too many requests. Please try again later.'
        } else {
          this.paymentErrorMessage = this.$t('home.paymentError') || 'There was an error submitting your payment confirmation. Please contact support.'
        }

        // Close modal to show error banner
        this.showPaymentModal = false

        // Scroll to top to see error message
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }
}
</script>

<style scoped>
/* Subscription Pricing Section */
.subscription-pricing-section {
  margin: 2rem 0 3rem 0;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.05) 0%, rgba(67, 233, 123, 0.05) 100%);
  border-radius: 16px;
  position: relative;
  z-index: 1;
}

.pricing-section-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #4a90e2;
  text-align: center;
  margin-bottom: 0.5rem;
}

.pricing-section-subtitle {
  font-size: 1rem;
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
}

.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.pricing-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.2);
  border-color: #4a90e2;
}

.pricing-card.recommended {
  border-color: #ff6b6b;
  border-width: 3px;
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.25);
  background: linear-gradient(135deg, #fff 0%, #fff5f5 100%);
}

.pricing-card.recommended:hover {
  box-shadow: 0 12px 32px rgba(255, 107, 107, 0.35);
  transform: translateY(-10px) scale(1.02);
}

.recommended-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: #fff;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.03); }
}

.recommended-badge svg {
  font-size: 0.9rem;
}

.plan-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-top: 0.5rem;
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 0.5rem;
}

.plan-duration {
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
}

.plan-pricing {
  text-align: center;
  margin-bottom: 1rem;
  padding: 1rem 0;
  border-top: 2px solid #f0f0f0;
  border-bottom: 2px solid #f0f0f0;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.price-amount {
  font-size: 2rem;
  font-weight: 800;
  color: #4a90e2;
}

.price-currency {
  font-size: 1rem;
  font-weight: 600;
  color: #666;
}

.plan-price-per-month {
  font-size: 0.9rem;
  color: #43e97b;
  font-weight: 600;
}

.plan-discount {
  text-align: center;
  background: linear-gradient(135deg, #43e97b 0%, #38d9a9 100%);
  color: #fff;
  padding: 0.6rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(67, 233, 123, 0.3);
}

.savings-amount {
  font-size: 0.85rem;
  opacity: 0.95;
}

.plan-features {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: auto;
}

.plan-features .feature-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: #555;
  background: rgba(74, 144, 226, 0.05);
  padding: 0.6rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.plan-features .feature-item:hover {
  background: rgba(74, 144, 226, 0.1);
  transform: translateX(4px);
}

.plan-features .check-icon {
  color: #43e97b;
  font-size: 1rem;
  flex-shrink: 0;
}

/* Strava ID Input Section */
.strava-id-input-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #4a90e2;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.strava-id-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #4a90e2;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.strava-id-description {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.strava-id-input-group {
  margin-bottom: 0.75rem;
}

.strava-id-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  transition: all 0.3s ease;
}

.strava-id-input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.strava-id-input.has-error {
  border-color: #ff6b6b;
  background: #fff5f5;
}

.strava-id-input.has-error:focus {
  border-color: #ff6b6b;
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.15);
}

.strava-id-error {
  color: #ff6b6b;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.strava-id-help {
  color: #666;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0;
}

.strava-id-help a {
  color: #4a90e2;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.strava-id-help a:hover {
  color: #357abd;
  text-decoration: underline;
}

/* Logged-in user info */
.logged-in-user-info {
  background: linear-gradient(135deg, #43e97b 0%, #38d9a9 100%);
  color: #fff;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(67, 233, 123, 0.3);
}

.logged-in-user-info .check-icon {
  font-size: 1.2rem;
}

.logged-in-user-info strong {
  font-family: 'Courier New', monospace;
}

/* Responsive Styles */
@media (max-width: 900px) {
  .subscription-pricing-section {
    margin: 1.5rem 0 2rem 0;
    padding: 1.5rem 0.8rem;
  }

  .pricing-section-title {
    font-size: 1.5rem;
    margin-bottom: 0.4rem;
  }

  .pricing-section-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .pricing-cards {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }

  .pricing-card {
    padding: 1.2rem;
  }

  .plan-name {
    font-size: 1.3rem;
  }

  .price-amount {
    font-size: 1.7rem;
  }

  .plan-features .feature-item {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
}

@media (max-width: 600px) {
  .subscription-pricing-section {
    margin: 1rem 0 1.5rem 0;
    padding: 1.2rem 0.6rem;
  }

  .pricing-section-title {
    font-size: 1.4rem;
  }

  .pricing-section-subtitle {
    font-size: 0.85rem;
    margin-bottom: 1.2rem;
  }

  .pricing-cards {
    gap: 1rem;
  }

  .pricing-card {
    padding: 1rem;
  }

  .recommended-badge {
    font-size: 0.65rem;
    padding: 0.35rem 0.8rem;
  }

  .plan-name {
    font-size: 1.2rem;
  }

  .plan-duration {
    font-size: 0.85rem;
  }

  .price-amount {
    font-size: 1.6rem;
  }

  .price-currency {
    font-size: 0.9rem;
  }

  .plan-price-per-month {
    font-size: 0.85rem;
  }

  .plan-discount {
    font-size: 0.85rem;
    padding: 0.5rem;
  }

  .plan-features .feature-item {
    font-size: 0.75rem;
    padding: 0.45rem;
  }
}

/* Current Subscription Banner */
.current-subscription-banner {
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border: 2px solid #4a90e2;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.15);
}

.subscription-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.subscription-badge.status-active {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.subscription-badge.status-pending {
  background: linear-gradient(135deg, #ffa500 0%, #ffb84d 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.4);
}

.subscription-badge.status-suspend {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
}

.status-icon {
  font-size: 1rem;
}

.subscription-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.info-row.pending-message {
  background: rgba(255, 165, 0, 0.1);
  padding: 0.75rem;
  border-radius: 8px;
  color: #ff8c00;
  font-weight: 600;
}

.pending-text {
  flex: 1;
}

.pending-text p {
  margin: 0 0 0.5rem 0;
}

.pending-text p:last-child {
  margin-bottom: 0;
}

.pending-text .pending-title {
  font-size: 1rem;
  font-weight: 700;
  color: #ff8c00;
  margin-bottom: 0.75rem;
}

.pending-text .pending-timeline {
  font-size: 0.95rem;
  color: #555;
  font-weight: 500;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.contact-info {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.contact-info .contact-icon {
  color: #4a90e2;
  margin-right: 0.25rem;
}

.contact-info strong {
  color: #333;
  margin-right: 0.25rem;
}

.contact-email {
  color: #4a90e2;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.contact-email:hover {
  color: #357abd;
  text-decoration: underline;
}

.suspended-banner {
  border-color: #ff6b6b;
}

.info-row.suspended-message {
  background: rgba(255, 107, 107, 0.1);
  padding: 0.75rem;
  border-radius: 8px;
  color: #c92a2a;
  font-weight: 600;
}

.suspended-text {
  flex: 1;
}

.suspended-text p {
  margin: 0 0 0.5rem 0;
}

.suspended-text p:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #666;
  min-width: 120px;
}

.info-value {
  font-weight: 700;
  color: #333;
}

.info-value.status-active {
  color: #43e97b;
}

.info-value.status-pending {
  color: #ffa500;
}

.info-value.highlight {
  color: #4a90e2;
  font-size: 1.05rem;
}

/* Payment Modal Styles */
.payment-modal-content {
  padding: 1rem 0;
}

.plan-summary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.plan-summary-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #4a90e2;
  margin-bottom: 1rem;
}

.plan-summary-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
}

.summary-value {
  font-weight: 700;
  color: #333;
  font-size: 1rem;
}

.summary-value.highlight {
  color: #4a90e2;
  font-size: 1.2rem;
}

.summary-value.save-amount {
  color: #43e97b;
}

.discount-item {
  grid-column: 1 / -1;
  background: linear-gradient(135deg, #43e97b 0%, #38d9a9 100%);
  color: #fff;
  padding: 0.75rem;
  border-radius: 8px;
}

.discount-item .summary-label,
.discount-item .summary-value {
  color: #fff;
}

.payment-instructions {
  margin-bottom: 2rem;
}

.instructions-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #4a90e2;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.instruction-step {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #4a90e2;
}

.instruction-step.important {
  background: #fff5f5;
  border-left-color: #ff6b6b;
}

.step-number {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #4a90e2 0%, #43e97b 100%);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}

.step-content {
  flex: 1;
}

.step-content h6 {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.step-content p {
  color: #666;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.bank-details {
  background: #fff;
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.bank-info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.bank-info-item:last-child {
  border-bottom: none;
}

.bank-label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
}

.bank-value {
  font-weight: 700;
  color: #333;
  font-family: 'Courier New', monospace;
}

.bank-value.copyable {
  cursor: pointer;
  color: #4a90e2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.bank-value.copyable:hover {
  color: #357abd;
}

.copy-icon {
  font-size: 0.85rem;
}

.transfer-note-box {
  background: #fff;
  border: 2px dashed #ff6b6b;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
}

.transfer-note-box code {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ff6b6b;
  background: transparent;
  padding: 0;
}

.copy-btn {
  background: linear-gradient(135deg, #4a90e2 0%, #43e97b 100%);
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.note-description {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.note-warning {
  color: #ff6b6b;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.qr-code-container {
  text-align: center;
  padding: 1rem;
  background: #fff;
  border-radius: 12px;
  border: 2px solid #e9ecef;
}

.qr-code {
  max-width: 300px;
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.qr-note {
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  margin-top: 1rem;
  margin-bottom: 0;
}

/* Payment Success Banner */
.payment-success-banner {
  background: linear-gradient(135deg, #e6ffed 0%, #d3f9d8 100%);
  border: 2px solid #43e97b;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  animation: slideDown 0.4s ease;
  box-shadow: 0 4px 16px rgba(67, 233, 123, 0.2);
}

.payment-error-banner {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  border: 2px solid #ff6b6b;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  animation: slideDown 0.4s ease;
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.2);
}

.banner-icon-wrapper {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-success-banner .banner-icon-wrapper {
  background: #43e97b;
  color: #fff;
}

.payment-error-banner .banner-icon-wrapper {
  background: #ff6b6b;
  color: #fff;
}

.banner-icon {
  font-size: 1.5rem;
}

.banner-content {
  flex: 1;
}

.banner-title {
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
}

.payment-success-banner .banner-title {
  color: #2b8a3e;
}

.payment-error-banner .banner-title {
  color: #c92a2a;
}

.banner-message {
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
  color: #333;
  line-height: 1.5;
}

.banner-note {
  font-size: 0.9rem;
  margin: 0;
  color: #666;
  line-height: 1.4;
}

.banner-email {
  color: #4a90e2;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.banner-email:hover {
  color: #357abd;
  text-decoration: underline;
}

.banner-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: #666;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.banner-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

/* Strava ID Error Banner */
.strava-id-error-banner {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  border: 2px solid #ff6b6b;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #c92a2a;
  font-weight: 600;
  animation: slideDown 0.3s ease;
}

.strava-id-error-banner .error-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 2px solid #f0f0f0;
}

.modal-actions .btn {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.modal-actions .btn-primary {
  background: linear-gradient(135deg, #4a90e2 0%, #43e97b 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.modal-actions .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
}

.copy-success-toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, #43e97b 0%, #38d9a9 100%);
  color: #fff;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(67, 233, 123, 0.4);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  animation: slideIn 0.3s ease;
  z-index: 9999;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .plan-summary-details {
    grid-template-columns: 1fr;
  }

  .payment-success-banner,
  .payment-error-banner {
    padding: 1rem;
    gap: 0.75rem;
  }

  .banner-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .banner-icon {
    font-size: 1.2rem;
  }

  .banner-title {
    font-size: 1.05rem;
  }

  .banner-message {
    font-size: 0.9rem;
  }

  .banner-note {
    font-size: 0.85rem;
  }

  .banner-close {
    top: 0.75rem;
    right: 0.75rem;
    width: 28px;
    height: 28px;
    font-size: 1rem;
  }

  .strava-id-input-section {
    padding: 1rem;
  }

  .strava-id-title {
    font-size: 1.05rem;
  }

  .strava-id-description {
    font-size: 0.85rem;
  }

  .strava-id-help {
    font-size: 0.8rem;
  }

  .logged-in-user-info {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .qr-code {
    max-width: 250px;
  }

  .strava-id-error-banner {
    padding: 0.85rem 1rem;
    font-size: 0.9rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }

  .copy-success-toast {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
  }
}

/* === New: AI Hero, Comparison, FAQ === */
.ai-hero {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 1.5rem;
  align-items: stretch;
  max-width: 1200px;
  margin: 0 auto 2rem auto;
}

.ai-hero-left {
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.08) 0%, rgba(67, 233, 123, 0.08) 100%);
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1.25rem;
}

.ai-hero-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.ai-hero-subtitle {
  color: #555;
  margin-bottom: 1rem;
}

.ai-hero-points {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 1rem;
}

.ai-hero-points li {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
}

.ai-hero-points .benefit-title {
  font-weight: 700;
  margin-right: 0.25rem;
}

.ai-hero-points .benefit-desc {
  color: #666;
}

.ai-hero-ctas {
  display: flex;
  gap: 0.75rem;
}

.ai-hero-right {
  display: grid;
  gap: 1rem;
}

.ai-metrics-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.ai-metrics-card .metric {
  background: #f8f9fa;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
}

.ai-metrics-card .metric-label {
  font-size: 0.8rem;
  color: #666;
}

.ai-metrics-card .metric-value {
  font-weight: 800;
  color: #333;
}

.ai-metrics-card .metric-delta {
  font-size: 0.8rem;
  font-weight: 700;
}
.ai-metrics-card .metric-delta.positive { color: #43e97b; }
.ai-metrics-card .metric-delta.neutral { color: #999; }

.ai-how-it-works {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1rem;
}

.ai-how-it-works .step {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.6rem 0.3rem;
}

.ai-how-it-works .step + .step {
  border-top: 1px dashed #dee2e6;
}

.ai-how-it-works .step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4a90e2 0%, #43e97b 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.ai-how-it-works .step-title {
  font-weight: 800;
  color: #333;
}

.ai-how-it-works .step-desc {
  color: #666;
  font-size: 0.9rem;
}

.comparison {
  max-width: 1200px;
  margin: 0 auto 1.5rem auto;
}

.comparison-title {
  text-align: center;
  font-weight: 800;
  color: #333;
  margin-bottom: 0.8rem;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.comparison-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1rem;
}

.comparison-card.pro {
  border-color: #ff6b6b;
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.15);
  background: linear-gradient(135deg, #fff 0%, #fff5f5 100%);
}

.comparison-card .comp-header {
  font-weight: 800;
  color: #333;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.comparison-card .comp-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.4rem;
}

.comparison-card .comp-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
}

/* Responsive for new blocks */
@media (max-width: 900px) {
  .ai-hero {
    grid-template-columns: 1fr;
  }
  .ai-hero-points {
    grid-template-columns: 1fr;
  }
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}
</style>
